import { type ReactNode, useCallback, useMemo } from 'react';

import useControlledState from '../../../hooks/useControlledState';
import { TableExtraColumn } from '../Table.constants';
import {
    type ColumnConfig,
    type ExpandableConfig,
    type Key,
    type TriggerEventHandler
} from '../Table.types';

const DEFAULT_COLUMN_WIDTH = 48;

type UseExpandProps<T> = {
    expandable?: ExpandableConfig<T>;
    createRowKey: (record: T, index?: number) => Key;
    button: (record: T, isExpanded: boolean) => ReactNode;
};

export const useExpand = <T>({ expandable, createRowKey, button }: UseExpandProps<T>) => {
    const {
        expandedRowRender,
        rowExpandable,
        expandedRowKeys,
        defaultExpandedRowKeys,
        onExpandedRowsChange,
        onExpand,
        columnWidth = DEFAULT_COLUMN_WIDTH
    } = expandable || {};

    const isTableExpandable = Boolean(expandedRowRender);

    const [innerExpandedRowKeys = [], setInnerExpandedRowKeys] = useControlledState(
        expandedRowKeys,
        defaultExpandedRowKeys,
        'Table',
        'expandedRowKeys'
    );

    const mergedExpandedKeys = useMemo(
        () => new Set(expandedRowKeys || innerExpandedRowKeys || []),
        [expandedRowKeys, innerExpandedRowKeys]
    );

    const onTriggerExpand: TriggerEventHandler<T> = useCallback(
        (record) => {
            const key = createRowKey(record, innerExpandedRowKeys?.indexOf?.(record as never));

            let newExpandedKeys: Key[];
            const hasKey = mergedExpandedKeys.has(key);
            if (hasKey) {
                mergedExpandedKeys.delete(key);
                newExpandedKeys = [...mergedExpandedKeys];
            } else {
                newExpandedKeys = [...mergedExpandedKeys, key];
            }

            setInnerExpandedRowKeys(newExpandedKeys);
            onExpand?.(!hasKey, record);
            onExpandedRowsChange?.(newExpandedKeys);
        },
        [
            createRowKey,
            mergedExpandedKeys,
            innerExpandedRowKeys,
            onExpand,
            setInnerExpandedRowKeys,
            onExpandedRowsChange
        ]
    );

    const expandColumn: ColumnConfig<T> = useMemo(
        () => ({
            key: TableExtraColumn.Expand,
            dataIndex: TableExtraColumn.Expand,
            title: '',
            width: columnWidth,
            render: (_value, record) => {
                if (rowExpandable && !rowExpandable(record)) {
                    return null;
                }
                const key = createRowKey(record, innerExpandedRowKeys?.indexOf?.(record as never));
                const isExpanded = mergedExpandedKeys.has(key);
                return button(record, isExpanded);
            }
        }),
        [createRowKey, innerExpandedRowKeys, mergedExpandedKeys, onTriggerExpand]
    );

    return {
        expandedRowRender,
        rowExpandable,
        isTableExpandable,
        mergedExpandedKeys,
        onTriggerExpand,
        expandColumn
    };
};
